import { useCallback, useMemo } from 'react';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import Select from 'src/components/primitives/Select/Select';
import {
	type TabOptionWithSelect,
	TabSelectItem,
} from 'src/components/primitives/Tabs/types';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

type Props<S extends string> = Omit<TabOptionWithSelect<S>, 'value'>;

export default function TabOptionWithSelect<S extends string>({
	selectedValue,
	onSelectedValueChange,
	selectOptions: optionsFromProps,
	label,
	disabled,
}: Props<S>) {
	const isMobileScreenSize = useIsMobileScreenSize();

	const options = useMemo(() => {
		return optionsFromProps.map((option) => ({
			label: option.label,
			value: option,
		}));
	}, [optionsFromProps]);

	const handleChange = useCallback(
		(value: TabSelectItem<S>) => {
			onSelectedValueChange(value.value);
		},
		[onSelectedValueChange]
	);

	const {
		selectOptions,
		selectedOption,
		value,
		onValueChange,
		open,
		onOpenChange,
	} = useSelect({
		selectedValue,
		onSelectedValueChange: handleChange,
		options,
	});

	return (
		<Select.Root
			open={open}
			onOpenChange={onOpenChange}
			value={value}
			onValueChange={onValueChange}
			disabled={disabled}
		>
			<Select.Trigger
				size="sm"
				className="border-0 bg-transparent p-0 hover:bg-transparent"
				valueClassName={
					selectedValue
						? 'text-text-default'
						: 'text-text-secondary hover:text-text-default'
				}
			>
				{selectedOption?.label ?? label}
			</Select.Trigger>
			<Select.Options>
				{selectOptions.map(({ original, label, value }) => {
					if (
						(isMobileScreenSize && original.screenSize === 'desktop') ||
						(!isMobileScreenSize && original.screenSize === 'mobile')
					) {
						return null;
					}

					return (
						<Select.Option
							key={value}
							value={value}
							disabled={original.disabled}
						>
							{label}
						</Select.Option>
					);
				})}
			</Select.Options>
		</Select.Root>
	);
}
